// Live documents on Now's map: a run that just happened sends a document along its integration's line. The page polls
// the activity every 15 s; between two polls a process's runs grew (or its newest run is newer): that many runs departed,
// up to MAX_BURST, a moment apart. On the first poll the runs of the last FRESH_MS count as just happened.

export const MAX_BURST = 3;
export const FRESH_MS = 90_000;
/** How long a departure is kept (its lap is over long before). */
export const KEEP_MS = 120_000;

export interface Seen { runs: number; last: number }
export interface Departure { key: string; at: number }

/** The runs that departed since the last poll, per process, and what is seen now. */
export function departuresOf(seen: ReadonlyMap<string, Seen>, flows: readonly { process: string; runs: number; last: number }[], now: number):
  { seen: Map<string, Seen>; departed: Map<string, Departure[]> } {
  const next = new Map<string, Seen>();
  const departed = new Map<string, Departure[]>();
  for (const f of flows) {
    const was = seen.get(f.process);
    next.set(f.process, { runs: Math.max(f.runs, was?.runs ?? 0), last: Math.max(f.last, was?.last ?? 0) });
    let n = 0;
    if (!was) n = now - f.last <= FRESH_MS ? 1 : 0;
    else if (f.runs > was.runs) n = f.runs - was.runs;
    else if (f.last > was.last) n = 1;
    n = Math.min(MAX_BURST, n);
    if (n > 0) departed.set(f.process, Array.from({ length: n }, (_, i) => ({ key: `${f.process}|${f.last}|${i}`, at: now + i * 1200 })));
  }
  return { seen: next, departed };
}

/** Departures kept: the new ones added, those older than KEEP_MS gone. */
export function keepDepartures(kept: ReadonlyMap<string, Departure[]>, added: ReadonlyMap<string, Departure[]>, now: number): Map<string, Departure[]> {
  const out = new Map<string, Departure[]>();
  for (const [p, ds] of [...kept, ...added]) {
    const merged = [...(out.get(p) ?? []), ...ds].filter((d, i, xs) => now - d.at < KEEP_MS && xs.findIndex((x) => x.key === d.key) === i);
    if (merged.length) out.set(p, merged);
  }
  return out;
}

export type Pace = 'live' | 'rhythm';
const PACE_KEY = 'mv.now.pace';
export function storedPace(): Pace {
  try { return localStorage.getItem(PACE_KEY) === 'rhythm' ? 'rhythm' : 'live'; } catch { return 'live'; }
}
export function readPace(p: URLSearchParams, fallback: Pace): Pace {
  const v = p.get('pace');
  return v === 'rhythm' || v === 'live' ? v : fallback;
}
export function withPace(p: URLSearchParams, v: Pace): URLSearchParams {
  const next = new URLSearchParams(p);
  next.set('pace', v);
  try { localStorage.setItem(PACE_KEY, v); } catch { /* a preference only */ }
  return next;
}
